'use client';

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../hooks/use-translation';
import { useHashScroll } from '../../hooks/use-hash-scroll';
import { useCommentDeepLink } from '../../hooks/use-comment-deep-link';
import AppPageContext from '../../context/app-page-context';
import { useReviews } from '../../hooks/use-reviews';
import { ReviewSlider } from './review-slider';
import { LeaveAReview } from './leave-a-review';
import { ReviewsModal } from './reviews-modal';
import { RatingsDashboard } from './ratings-dashboard';
const kNumOfReviewsPerPage = 50;

// -----------------------------------------------------------------------------
export const Reviews = (): JSX.Element => {
  const { t } = useTranslation();
  const { app } = useContext(AppPageContext);
  useHashScroll('reviews');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [allReviewsLoaded, setAllReviewsLoaded] = useState<boolean>(
    kNumOfReviewsPerPage > app.initialReviews.totalRateCount,
  );
  const [page, setPage] = useState<number>(1);

  const { reviews, isLoading, loadMoreReviews, slideReviews } = useReviews(
    app.initialReviews?.comments ?? null,
  );

  const handleLoadMore = useCallback(async (): Promise<void> => {
    const newPage = page + 1;
    setPage(newPage);
    await loadMoreReviews(app, newPage);
    if (newPage * kNumOfReviewsPerPage >= app.initialReviews.totalRateCount) {
      setAllReviewsLoaded(true);
    }
  }, [app.id, page, loadMoreReviews, allReviewsLoaded]);

  const handleToggleOpen = (): void => {
    setIsOpen((cur) => !cur);
  };

  const handleClose = (): void => {
    setIsOpen(false);
  };

  // Review deep-link (?commentId=...): open the modal and keep loading pages
  // until the target comment is in the list, then ReviewsModal scrolls/highlights it.
  const deepLinkCommentId = useCommentDeepLink();

  useEffect(() => {
    if (deepLinkCommentId != null) {
      setIsOpen(true);
    }
  }, [deepLinkCommentId]);

  const isTargetLoaded = useMemo(
    () =>
      deepLinkCommentId != null &&
      !!reviews?.some((r) => r.comment_ID === deepLinkCommentId),
    [reviews, deepLinkCommentId],
  );

  useEffect(() => {
    if (deepLinkCommentId == null || isTargetLoaded) return;
    if (allReviewsLoaded || isLoading) return;
    void handleLoadMore();
  }, [
    deepLinkCommentId,
    isTargetLoaded,
    allReviewsLoaded,
    isLoading,
    handleLoadMore,
  ]);

  // if (!app.initialReviews || app.initialReviews.totalRateCount === 0) {
  //   return <></>;
  // }

  return (
    <section className="reviews-section" id="reviews">
      <h2>{t('reviews')}</h2>

      <RatingsDashboard
        initialReviews={app.initialReviews}
        onToggleOpen={handleToggleOpen}
      />

      <LeaveAReview />

      <ReviewSlider reviews={slideReviews} />

      <ReviewsModal
        isOpen={isOpen}
        reviews={reviews}
        isLoading={isLoading}
        onClickClose={handleClose}
        loadMoreReviews={handleLoadMore}
        allReviewsLoaded={allReviewsLoaded}
        highlightCommentId={deepLinkCommentId}
      />
    </section>
  );
};
